import { MainLayout, Meta, StructuredMeta } from 'app/components'
import { CategoryOffersQueryOutput, OfferCategoryQueryOutput } from './queries'
import { OfferBlockRenderer, SearchComponent } from 'app/features/offer/blocks'
import { OfferCard, OfferList } from 'app/features/offer/components'
import { FlatList, RefreshControl } from 'react-native'
import { Frown } from '@tamagui/lucide-icons'
import {
  Container,
  Spinner,
  Button,
  SizableText,
  YStack,
  isWeb,
  Stack,
  colors,
  Spacer,
} from '@my/ui'
import { CategoryTabs } from '../../containers'

interface OfferCategoryScreenProps {
  loading: boolean
  category?: OfferCategoryQueryOutput['OfferCategories']['docs'][0]
  offers: CategoryOffersQueryOutput['fetchOffers']['docs']
  error: any
  hasMore: boolean
  loadingMore: boolean
  onLoadMore: () => void

  refreshing: boolean
  onPullToRefresh: () => void
}

export function OfferCategoryScreen(props: OfferCategoryScreenProps) {
  const hasMoreRender = props.hasMore && (
    <Button
      theme="primary"
      maxWidth={500}
      w="100%"
      mx="auto"
      loading={props.loadingMore}
      onPress={props.onLoadMore}
    >
      Load more
    </Button>
  )

  const noResults =
    !props.loading && props.category?.content?.length === 0 && props.offers?.length === 0

  const noResultRender = noResults && (
    <YStack flex={1} alignItems="center" w="100%" gap="$3.5" $gtSm={{ gap: '$6' }}>
      <YStack width="100%" alignItems="center">
        <Stack
          p="$2"
          backgroundColor="$white"
          borderRadius={100_100}
          shadowColor="black"
          shadowOpacity={0.16}
          shadowOffset={{ width: -1, height: 2 }}
          shadowRadius={8}
        >
          <Frown color={colors.primary} size={48} />
        </Stack>
        <SizableText textAlign="center" pt="$4" size="$9" fow="600">
          Sorry, there’s no offers available at the moment
        </SizableText>
        <SizableText textAlign="center" pt="$2" color="$overlay10" size="$5">
          But don’t worry, refresh and try again!
        </SizableText>
      </YStack>
      {isWeb && <SearchComponent heading={props.category?.name} />}
    </YStack>
  )

  const loadingRender = props.loading && (
    <YStack py="$3.5">
      <Spinner size="large" />
    </YStack>
  )

  return (
    <MainLayout>
      {props.category && (
        <>
          <Meta
            title={props.category?.meta?.title || props.category?.name || 'Offers'}
            description={props.category?.meta?.description}
          />
          <StructuredMeta
            type="article"
            title={props.category.name}
            description={props.category.meta?.description || ''}
            image={props.category.logo?.url}
            date={props.category.updatedAt}
          />
        </>
      )}
      {isWeb && <CategoryTabs />}

      {!isWeb && (
        <FlatList
          data={props.offers || []}
          keyExtractor={(item) => item.id}
          refreshControl={
            <RefreshControl refreshing={props.refreshing} onRefresh={props.onPullToRefresh} />
          }
          renderItem={({ item }) => (
            <Container flex={1} size="base" gutter>
              <OfferCard {...item} />
            </Container>
          )}
          ItemSeparatorComponent={() => <Spacer size="$3.5" />}
          ListHeaderComponent={
            <Container gap="$4" size="base" spaceTop>
              {props.category?.content && props.category?.content.length > 0 && (
                <OfferBlockRenderer content={props.category.content} />
              )}
              <Container pb="$3.5" $gtSm={{ pb: '$6' }} gutter>
                <SizableText size="$6" fow="600" $gtLg={{ size: '$9', fow: '600' }}>
                  More {props.category?.name} Deals
                </SizableText>
              </Container>
            </Container>
          }
          ListFooterComponent={
            <Container
              gutter
              spaceBottom
              size="base"
              flex={1}
              gap="$3.5"
              pt="$3.5"
              $gtSm={{ pt: '$6', gap: '$6' }}
            >
              {hasMoreRender}
              {noResultRender}
              {loadingRender}
            </Container>
          }
        />
      )}
      {isWeb && (
        <Container size="base" spaceTop spaceBottom gap="$3.5" $gtSm={{ gap: '$6' }}>
          {props.category?.content && props.category?.content.length > 0 && (
            <OfferBlockRenderer content={props.category.content} />
          )}
          {props.offers.length > 0 && (
            <Container gutter gap="$3.5" $gtSm={{ gap: '$6' }}>
              <YStack flex={1} width="100%" gap="$3.5">
                <SizableText size="$6" fow="600" $gtLg={{ size: '$9', fow: '600' }}>
                  More {props.category?.name} Deals
                </SizableText>
                <OfferList items={props.offers} />
              </YStack>
              {hasMoreRender}
            </Container>
          )}
          {(props.loading || noResults) && (
            <Container flex={1} gap="$3.5" $gtSm={{ gap: '$6' }} gutter size="base">
              {noResultRender}
              {loadingRender}
            </Container>
          )}
        </Container>
      )}
    </MainLayout>
  )
}
